import React, { useEffect, useState } from 'react';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Autocomplete, CircularProgress, TextField, Tooltip, Typography } from '@mui/material';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_CAMERA, IS_ANNOTATION, POLYGON_DETAILS, SHOW_ZONE_INPUT, ZONE_DATA } from 'store/actions';
import { getZoneData } from 'api/sentinelAPI';
import { CheckCircle } from '@mui/icons-material';

const Card = () => {
  const [inputZone, setZoneValue] = useState('');
  const initialPolygonDetails = { zone_name: '', colourHex: '' };
  const [autocompleteOptions, setAutocompleteOptions] = useState(['Phones', 'Sales', 'Accessories']);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedZoneIndex, setSelectedZoneIndex] = useState(null);
  const [isDefineAreaModalOpen, setIsDefineAreaModalOpen] = useState(false);
  const [currentZoneName, setCurrentZoneName] = useState('');
  const [zones, setZones] = useState([]);
  const [polygonDetails, setPolygonDetails] = useState(initialPolygonDetails);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const savedPolygons = useSelector((state) => state.cameraReducer.savedPolygons);
  const cameraData = useSelector((state) => state.cameraReducer.cameraData);
  const showZoneInput = useSelector((state) => state.cameraReducer.showZoneInput);
  const [error, setError] = useState({
    zone_name: false,
    colourHex: false
  });
  const [newZones, setNewZones] = useState([]);

  const updateAutocompleteOptions = (newOption) => {
    const updatedOptions = Array.from(new Set([...autocompleteOptions, newOption]));
    setAutocompleteOptions(updatedOptions);
  };

  const getRandomColorClass = () => {
    const colorClasses = [
      'bg-red-500',
      'bg-green-500',
      'bg-blue-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-indigo-500',
      'bg-pink-500',
      'bg-orange-500'
    ];
    const randomIndex = Math.floor(Math.random() * colorClasses.length);
    return colorClasses[randomIndex];
  };

  useEffect(() => {
    const fetchZones = async () => {
      try {
        const data = await getZoneData({ cameraId: cameraData?._id });
        setZones(data[0]?.zones);
      } catch (error) {
        console.log('Error Fetching Zone Data: ', error);
      }
    };
    fetchZones();
  }, [cameraData]);

  const handleZoneSubmit = (newValue) => {
    if (!newValue.trim()) return;

    const newZone = {
      icon: <div className={`h-full w-full rounded-full ${getRandomColorClass()}`}></div>,
      text: newValue
    };

    setZones((zones) => [...zones, newZone]);
    setZoneValue('');
    updateAutocompleteOptions(newValue);
    setShowAutocomplete(false);
    setCurrentZoneName(newValue);
    setIsDefineAreaModalOpen(true);
  };

  const addEmptyZone = () => {
    dispatch({ type: ADD_CAMERA, payload: { mode: 'Add Zone', addCamera: true } });
  };

  const renderAutocompleteForZones = () => {
    if (!showAutocomplete) return null;
    return (
      <Autocomplete
        freeSolo
        options={autocompleteOptions}
        value={inputZone}
        onChange={(event, newValue) => handleZoneSubmit(newValue)}
        onInputChange={(event, newValue) => setZoneValue(newValue)}
        inputValue={inputZone}
        renderInput={(params) => (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleZoneSubmit(inputZone);
            }}
          >
            <TextField
              {...params}
              variant="standard"
              size="small"
              InputProps={{
                ...params.InputProps,
                className: 'autocomplete-input',
                classes: { notchedOutline: 'no-border' },
                style: {}
              }}
              placeholder=""
            />
          </form>
        )}
      />
    );
  };

  const confirmRemoveZone = async () => {
    // const deleteZone = await deleteZoneData({ _id: zones[selectedZoneIndex]._id });
    const updatedZones = zones.filter((_, i) => i !== selectedZoneIndex);
    setZones(updatedZones);
    setIsModalOpen(false);
    setAutocompleteOptions(autocompleteOptions.filter((option) => option !== zones[selectedZoneIndex].text));
  };

  const openModal = (index) => {
    setIsModalOpen(true);
    setSelectedZoneIndex(index);
  };

  const handleSendData = async () => {
    setLoading(true);
    // const data = await addCameraWithZone({
    //   cameraData,
    //   zonesData: savedPolygons.filter((polygonObj, index) => index >= 1)
    // });
    setNewZones([]);
    dispatch({ type: IS_ANNOTATION, isAnnotation: false });
    setLoading(false);
    document.body.style.cursor = 'default';
  };

  const handleSavePolygon = () => {
    if (polygonDetails.colourHex && polygonDetails.zone_name) {
      if (polygonDetails.zone_name.trim() === '' || polygonDetails.colourHex.trim() === '') {
        setError({
          zone_name: polygonDetails.zone_name.trim() === '',
          colourHex: polygonDetails.colourHex.trim() === ''
        });
        return;
      }
      dispatch({ type: POLYGON_DETAILS, polygonDetails: polygonDetails });
      dispatch({ type: SHOW_ZONE_INPUT, showZoneInput: false });
      setNewZones([...newZones, polygonDetails]);
      setPolygonDetails(initialPolygonDetails);
    }
    if (!polygonDetails.colourHex) {
      alert('Please fill all the fields');
      setError({ ...error, colourHex: true });
    }
    if (!polygonDetails.zone_name) {
      alert('Please fill all the fields');
      setError({ ...error, zone_name: true });
    }
  };

  const handleRemoveZone = (e) => {
    const index = e.target.getAttribute('data-index');
    if (index !== null) {
      setNewZones(newZones.filter((_, i) => i !== Number(index)));
    }
  };

  return (
    <div className="rounded-lg p-2 text-white w-full h-full overflow-y-auto lg:p-6 md:p-4 sm:p-2">
      <div className="text-2xl font-semibold flex items-center justify-between mb-2 lg:text-3xl md:text-2xl sm:text-xl">
        <Typography variant="h3" className="text-white">
          Zone Details
        </Typography>
        <Tooltip title="Add Zone">
          <div onClick={addEmptyZone} className="cursor-pointer">
            <AddCircleIcon fontSize="large" />
          </div>
        </Tooltip>
      </div>

      <div className="text-md flex justify-between lg:text-lg md:text-md sm:text-sm">Zones</div>

      <div className="flex p-0 mb-4 flex-wrap gap-2 lg:gap-4 md:gap-3 sm:gap-1">
        {zones?.map((zone, index) => (
          <div
            key={index}
            className="text-sm flex items-center justify-left rounded-full bg-neutral-500 text-white p-[0.5rem] pr-2 m-1 lg:p-[0.8rem] md:p-[0.6rem] sm:p-[0.4rem]"
          >
            <div
              className="flex items-center justify-center h-5 w-5 mr-1 rounded-full lg:h-6 lg:w-6 md:h-5 md:w-5 sm:h-4 sm:w-4"
              style={{ backgroundColor: `${zone.colourHex}` }}
            ></div>
            <span>{zone.name}</span>
            <CancelIcon className="cursor-pointer" onClick={() => openModal(index)} style={{ marginLeft: '7px' }} />
          </div>
        ))}
        {showAutocomplete && (
          <div
            key="autocomplete"
            className="text-sm flex items-center justify-left rounded-full bg-neutral-500 text-white p-[0.5rem] pr-2 m-1 lg:p-[0.8rem] md:p-[0.6rem] sm:p-[0.4rem]"
          >
            <span>{renderAutocompleteForZones()}</span>
          </div>
        )}
      </div>

      <div className="flex justify-between text-md lg:text-lg md:text-md sm:text-sm">Zones to be added</div>

      <div className="flex p-0 mb-4 flex-wrap gap-2 relative lg:gap-4 md:gap-3 sm:gap-1">
        {showZoneInput && (
          <div className="text-sm flex items-center justify-left rounded-full bg-neutral-500 text-white p-[0.5rem] pr-2 m-1 lg:p-[0.8rem] md:p-[0.6rem] sm:p-[0.4rem]">
            <div className="relative w-6 h-6 border-red-500 mr-2 lg:w-8 lg:h-8 md:w-7 md:h-7 sm:w-5 sm:h-5">
              <div
                className="flex items-center absolute rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 justify-center h-6 w-6 z-[1] lg:h-8 lg:w-8 md:h-7 md:w-7 sm:h-5 sm:w-5"
                style={{ pointerEvents: 'none', backgroundColor: `${polygonDetails.colourHex || '#000'}` }}
              ></div>
              <input
                type="color"
                className="bg-transparent absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 justify-center h-6 w-6 border-none z-0 lg:h-8 lg:w-8 md:h-7 md:w-7 sm:h-5 sm:w-5"
                onChange={(e) => setPolygonDetails({ ...polygonDetails, colourHex: e.target.value })}
              />
            </div>
            <TextField
              sx={{
                '& .MuiInputBase-input': {
                  color: 'white'
                },
                '& .MuiInputLabel-root:before': {
                  color: 'white'
                },
                '& .MuiInputBase-input:focus': {
                  borderColor: 'red'
                },

                '& .MuiInputLabel-root': {
                  color: 'white'
                }
              }}
              label="Enter zone name"
              onChange={(e) => {
                if (e.target.value) {
                  setError({ zone_name: false });
                  setPolygonDetails((prevDetails) => ({ ...prevDetails, zone_name: e.target.value }));
                } else {
                  setError({ zone_name: true });
                }
              }}
              variant="outlined"
              size="small"
              error={error.zone_name || error.colourHex}
              helperText={
                error.zone_name && error.colourHex
                  ? 'Please provide all the details'
                  : error.zone_name
                  ? 'Please provide zone name'
                  : error.colourHex
                  ? 'Please provide zone color'
                  : ''
              }
            />
            <CheckCircle className="cursor-pointer" style={{ marginLeft: '7px' }} onClick={handleSavePolygon} />
          </div>
        )}

        <div onClick={handleRemoveZone}>
          {newZones.length > 0 &&
            newZones.map((zone, index) => (
              <div
                key={index}
                className="text-sm flex items-center justify-left rounded-full bg-neutral-500 text-white p-[0.5rem] pr-2 m-1 lg:p-[0.8rem] md:p-[0.6rem] sm:p-[0.4rem]"
              >
                <div
                  className="flex items-center justify-center h-5 w-5 mr-1 rounded-full lg:h-6 lg:w-6 md:h-5 md:w-5 sm:h-4 sm:w-4"
                  style={{ backgroundColor: `${zone.colourHex}` }}
                ></div>
                <span>{zone.zone_name}</span>
                <CancelIcon className="cursor-pointer" data-index={index} style={{ marginLeft: '7px' }} />
              </div>
            ))}
        </div>
        <div className="flex items-center w-full justify-end">
          <Button
            color="primary"
            variant="contained"
            onClick={handleSendData}
            disabled={savedPolygons.length < 2 || loading}
            hidden={savedPolygons.length < 2}
            sx={(theme) => ({
              backgroundColor: `${savedPolygons.length > 1 ? theme.palette.primary.main : 'grey'} !important`,
              cursor: savedPolygons.length > 1 ? 'pointer' : 'not-allowed'
            })}
            size="small"
          >
            {loading ? <CircularProgress size={25} /> : 'Send Data'}
          </Button>
        </div>
      </div>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 bg-white border-2 border-black shadow-lg p-4 flex flex-col items-center gap-4 lg:w-1/2 md:w-2/3 sm:w-5/6">
          <h1 id="modal-modal-title" className="text-xl font-semibold text-center lg:text-2xl md:text-xl sm:text-lg">
            Confirm Removal
          </h1>
          <p id="modal-modal-description" className="text-sm lg:text-lg md:text-md sm:text-sm">
            Do you really want to remove this zone?
          </p>
          <div className="flex justify-center gap-2">
            <Button variant="outlined" color="success" onClick={confirmRemoveZone} className="mt-2">
              Yes
            </Button>
            <Button variant="outlined" color="error" onClick={() => setIsModalOpen(false)} className="mt-2">
              No
            </Button>
          </div>
        </Box>
      </Modal>

      <Modal
        open={isDefineAreaModalOpen}
        onClose={() => setIsDefineAreaModalOpen(false)}
        aria-labelledby="define-area-modal-title"
        aria-describedby="define-area-modal-description"
      >
        <Box className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 bg-white border-2 border-black shadow-lg p-4 flex flex-col items-center gap-4 lg:w-1/2 md:w-2/3 sm:w-5/6">
          <h2 id="define-area-modal-title" className="text-xl font-semibold text-center lg:text-2xl md:text-xl sm:text-lg">
            Define Area
          </h2>
          <p id="define-area-modal-description" className="text-sm lg:text-lg md:text-md sm:text-sm">
            Please define the area of the {currentZoneName} zone in the above camera view.
          </p>
          <Button
            variant="contained"
            onClick={() => {
              setIsDefineAreaModalOpen(false);
              dispatch({ type: ADD_CAMERA, payload: { mode: 'Add Zone', addCamera: true } });
              dispatch({ type: ZONE_DATA, zoneData: { name: currentZoneName } });
              console.log(currentZoneName);
            }}
            className="mt-2"
          >
            OK
          </Button>
        </Box>
      </Modal>
    </div>
  );
};

export default Card;
